/* Iter 117 — Go-Live Checklist page.
 *
 * Master task tracker for the Infinity Imaging go-live preparation.
 *  • Top: overall progress bar + chips per status
 *  • Body: collapsible category sections, each task = checkbox + title +
 *          status pill + owner + due date + expandable notes thread
 *  • Toolbar: search, status filter, "+ Add task", "Import more tasks"
 *  • Import flow: paste pasteable text → preview which are new / dupes →
 *                 commit only the new ones.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, Circle, Plus, Search, Filter, Trash2, Calendar, User,
  MessageSquarePlus, X, Upload, AlertCircle, Clock, Pause, Ban, ChevronDown, ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual config for each status — same order = pill cycle order
const STATUSES = [
  { id: 'not_started',  label: 'Not started',  icon: Circle,       color: '#6b7280', bg: '#f3f4f6' },
  { id: 'in_progress',  label: 'In progress',  icon: Clock,        color: '#2563eb', bg: '#dbeafe' },
  { id: 'pending',      label: 'Pending',      icon: Pause,        color: '#d97706', bg: '#fef3c7' },
  { id: 'completed',    label: 'Completed',    icon: CheckCircle2, color: '#15803d', bg: '#dcfce7' },
  { id: 'blocked',      label: 'Blocked',      icon: Ban,          color: '#b91c1c', bg: '#fee2e2' },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.id, s]));

const CATEGORY_ICON = {
  'STAFFING': '👥', 'MEDICARE / REGISTRATIONS': '🩺', 'IT / PHONES': '💻',
  'WEBSITE / DIGITAL': '🌐', 'MARKETING & PRINT': '🖨️', 'RECEPTION': '🛎️',
  'CT': '🩻', 'X-RAY': '☢️', 'ULTRASOUND': '🫀', 'LP SERVICE': '💉',
  'BLINDS & PRIVACY': '🪟', 'STAFF FACILITIES': '🚪', 'STORAGE': '📦',
  'TOILETS': '🚽', 'FIRE & SECURITY': '🚨', 'CLEANING': '🧹',
  'CLINICAL EQUIPMENT': '⚕️', 'PRP SERVICE': '🧬',
  'CONSUMABLES (DELIVERY BY 3 JULY)': '📦', 'SIGNAGE': '🪧',
  'OFFICEWORKS': '📋', 'BUNNINGS': '🔨', 'POWER': '⚡',
  'FINAL GO-LIVE WEEK': '🚀',
};

export default function GoLivePage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, percent: 0, by_category: {}, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [collapsed, setCollapsed] = useState(new Set());
  const [showAddTask, setShowAddTask] = useState(null);   // category name or null
  const [showImport, setShowImport] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(new Set());

  const load = async () => {
    try {
      const r = await axios.get(`${API}/golive/tasks`, { withCredentials: true });
      setTasks(r.data.tasks);
      setStats(r.data.stats);
      setLoading(false);
    } catch (e) {
      // 403 → user is logged in but isn't on the Go-Live allowlist.
      // Bounce them back to the dashboard so they can't sit on a useless page.
      if (e?.response?.status === 403) {
        navigate('/', { replace: true });
        return;
      }
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // ---- task ops ----
  const cycleStatus = async (t) => {
    const ids = STATUSES.map(s => s.id);
    const next = ids[(ids.indexOf(t.status) + 1) % ids.length];
    await patchTask(t.task_id, { status: next });
  };
  const toggleComplete = async (t) => {
    const next = t.status === 'completed' ? 'not_started' : 'completed';
    await patchTask(t.task_id, { status: next });
  };
  const patchTask = async (id, patch) => {
    const r = await axios.patch(`${API}/golive/tasks/${id}`, patch, { withCredentials: true });
    setTasks(prev => prev.map(t => t.task_id === id ? r.data : t));
    // recompute stats — single source of truth is the backend, refresh
    load();
  };
  const deleteTask = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await axios.delete(`${API}/golive/tasks/${t.task_id}`, { withCredentials: true });
      setTasks(prev => prev.filter(x => x.task_id !== t.task_id));
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not delete (PM+ only)');
    }
  };

  // ---- filtering ----
  const filteredByCat = useMemo(() => {
    const out = {};
    const s = search.toLowerCase().trim();
    for (const t of tasks) {
      if (statusFilter !== 'all' && t.status !== statusFilter) continue;
      if (s && !t.title.toLowerCase().includes(s)
            && !(t.owner||'').toLowerCase().includes(s)
            && !t.category.toLowerCase().includes(s)) continue;
      (out[t.category] = out[t.category] || []).push(t);
    }
    return out;
  }, [tasks, search, statusFilter]);

  const sortedCategories = useMemo(() => Object.keys(filteredByCat).sort(), [filteredByCat]);

  return (
    <div className="min-h-screen bg-background" data-testid="golive-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">

        {/* Header / progress */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#0d2644' }}>Go-Live Checklist</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Master preparation tracker for the Infinity Imaging launch. Tick, note, and import as you progress.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowImport(true)} data-testid="golive-import-btn"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border"
                style={{ borderColor: '#52c3c555', color: '#0d2644' }}>
                <Upload className="w-4 h-4" /> Import tasks
              </button>
            </div>
          </div>

          {/* Overall progress bar */}
          <div className="rounded-xl border bg-card p-4" data-testid="golive-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold" style={{ color: '#0d2644' }}>
                {stats.completed} of {stats.total} tasks complete
              </div>
              <div className="text-2xl font-bold" style={{ color: '#52c3c5' }}>{stats.percent}%</div>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-500" style={{
                width: `${stats.percent}%`,
                background: 'linear-gradient(90deg, #0d2644 0%, #52c3c5 100%)',
              }} />
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {STATUSES.map(s => {
                const n = stats.by_status[s.id] || 0;
                if (!n) return null;
                return (
                  <button key={s.id}
                    onClick={() => setStatusFilter(statusFilter === s.id ? 'all' : s.id)}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${statusFilter === s.id ? 'ring-2 ring-offset-1' : ''}`}
                    style={{ color: s.color, backgroundColor: s.bg, borderColor: s.color + '33' }}>
                    {s.label}: {n}
                  </button>
                );
              })}
              {statusFilter !== 'all' && (
                <button onClick={() => setStatusFilter('all')} className="text-xs text-muted-foreground hover:text-foreground underline">
                  Clear filter
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input placeholder="Search tasks, owners or categories…" value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="golive-search" />
        </div>

        {loading && <div className="text-center text-muted-foreground py-12">Loading…</div>}

        {/* Categories */}
        {!loading && sortedCategories.map(cat => {
          const list = filteredByCat[cat];
          const done = list.filter(t => t.status === 'completed').length;
          const pct = list.length ? Math.round(done * 100 / list.length) : 0;
          const isCollapsed = collapsed.has(cat);
          return (
            <section key={cat} className="mb-5 rounded-xl border bg-card overflow-hidden" data-testid={`golive-cat-${cat.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
              <button onClick={() => setCollapsed(prev => {
                const ns = new Set(prev);
                ns.has(cat) ? ns.delete(cat) : ns.add(cat);
                return ns;
              })}
                className="w-full text-left px-4 py-3 hover:bg-muted/40 flex items-center gap-3">
                {isCollapsed ? <ChevronRight className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                <span className="text-xl">{CATEGORY_ICON[cat] || '📋'}</span>
                <span className="font-semibold flex-1" style={{ color: '#0d2644' }}>{cat}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{done}/{list.length}</span>
                <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, backgroundColor: '#52c3c5' }} />
                </div>
              </button>
              {!isCollapsed && (
                <div className="border-t">
                  {list.map(t => (
                    <TaskRow key={t.task_id} t={t}
                      onToggle={() => toggleComplete(t)}
                      onCycle={() => cycleStatus(t)}
                      onPatch={(p) => patchTask(t.task_id, p)}
                      onDelete={() => deleteTask(t)}
                      notesOpen={expandedNotes.has(t.task_id)}
                      setNotesOpen={() => setExpandedNotes(prev => {
                        const ns = new Set(prev);
                        ns.has(t.task_id) ? ns.delete(t.task_id) : ns.add(t.task_id);
                        return ns;
                      })}
                      reload={load} />
                  ))}
                  <div className="px-4 py-2 border-t">
                    <button onClick={() => setShowAddTask(cat)} data-testid={`golive-add-${cat.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
                      className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                      <Plus className="w-3.5 h-3.5" /> Add task to {cat}
                    </button>
                  </div>
                </div>
              )}
            </section>
          );
        })}

        {!loading && !sortedCategories.length && (
          <div className="text-center text-muted-foreground py-12">No tasks match your filters.</div>
        )}
      </div>

      {showAddTask && <AddTaskModal category={showAddTask} onClose={() => setShowAddTask(null)} onAdded={() => { setShowAddTask(null); load(); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onCommitted={() => { setShowImport(false); load(); }} />}
    </div>
  );
}


/* ──────────────────────── Task row ──────────────────────────────── */
function TaskRow({ t, onToggle, onCycle, onPatch, onDelete, notesOpen, setNotesOpen, reload }) {
  const status = STATUS_MAP[t.status] || STATUS_MAP.not_started;
  const StatusIcon = status.icon;
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(t.title);
  const [editOwner, setEditOwner] = useState(t.owner || '');
  const [editDue, setEditDue] = useState(t.due || '');

  const saveEdit = async () => {
    const patch = {};
    if (editTitle !== t.title) patch.title = editTitle.trim();
    if ((editOwner || null) !== t.owner) patch.owner = editOwner.trim();
    if ((editDue || null) !== t.due) patch.due = editDue.trim();
    if (Object.keys(patch).length) await onPatch(patch);
    setEditing(false);
  };

  const isDone = t.status === 'completed';
  return (
    <div className={`px-4 py-3 flex items-start gap-3 border-b last:border-b-0 hover:bg-muted/30 ${isDone ? 'opacity-70' : ''}`}
         data-testid={`golive-task-${t.task_id}`}>
      <button onClick={onToggle} className="mt-0.5 shrink-0" data-testid={`golive-toggle-${t.task_id}`}
        title={isDone ? 'Mark as not started' : 'Mark complete'}>
        {isDone ? <CheckCircle2 className="w-5 h-5" style={{ color: '#15803d' }} />
                : <Circle className="w-5 h-5 text-muted-foreground hover:text-foreground" />}
      </button>

      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-2">
            <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="text-sm" autoFocus />
            <div className="flex gap-2 flex-wrap">
              <Input value={editOwner} onChange={e => setEditOwner(e.target.value)} placeholder="Owner" className="text-xs w-40" />
              <Input value={editDue} onChange={e => setEditDue(e.target.value)} placeholder="Due" className="text-xs w-32" />
              <button onClick={saveEdit} className="text-xs font-semibold px-3 py-1 rounded bg-[#0d2644] text-white">Save</button>
              <button onClick={() => { setEditing(false); setEditTitle(t.title); setEditOwner(t.owner||''); setEditDue(t.due||''); }}
                      className="text-xs px-3 py-1 rounded border">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className={`text-sm leading-snug ${isDone ? 'line-through text-muted-foreground' : ''}`}
                 onDoubleClick={() => setEditing(true)} title="Double-click to edit">
              {t.title}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
              {t.owner && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {t.owner}</span>}
              {t.due && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.due}</span>}
              <button onClick={() => setEditing(true)} className="underline hover:text-foreground">Edit</button>
              {(t.notes || []).length === 0 && (
                <button onClick={setNotesOpen} className="inline-flex items-center gap-1 underline hover:text-foreground" data-testid={`golive-notes-toggle-${t.task_id}`}>
                  <MessageSquarePlus className="w-3 h-3" /> Add note
                </button>
              )}
            </div>
            {/* Iter 123 — notes auto-expand whenever any exist so they're never hidden */}
            {((t.notes || []).length > 0 || notesOpen) && <NotesThread t={t} reload={reload} />}
          </>
        )}
      </div>

      <button onClick={onCycle} className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap inline-flex items-center gap-1"
        style={{ color: status.color, backgroundColor: status.bg, borderColor: status.color + '33' }}
        title="Click to cycle: Not started → In progress → Pending → Completed → Blocked"
        data-testid={`golive-status-${t.task_id}`}>
        <StatusIcon className="w-3 h-3" /> {status.label}
      </button>

      <button onClick={onDelete} className="shrink-0 text-muted-foreground hover:text-red-600 p-1"
        title="Delete task (PM+ only)" data-testid={`golive-delete-${t.task_id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}


/* ──────────────────────── Notes thread ──────────────────────────── */
const PRIORITY_STYLES = {
  red:   { dot: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'High priority' },
  amber: { dot: '#f59e0b', bg: '#fffbeb', border: '#fde68a', label: 'Medium priority' },
  green: { dot: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', label: 'Done / low priority' },
  none:  { dot: '#cbd5e1', bg: '#ffffff', border: '#e5e7eb', label: 'No priority' },
};
const PRIORITY_KEYS = ['red', 'amber', 'green', 'none'];

function NotesThread({ t, reload }) {
  const [newNote, setNewNote] = useState('');
  const [newPriority, setNewPriority] = useState('none');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!newNote.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/golive/tasks/${t.task_id}/notes`,
        { text: newNote.trim(), priority: newPriority },
        { withCredentials: true });
      setNewNote(''); setNewPriority('none'); reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not add note');
    }
    setSaving(false);
  };
  const del = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await axios.delete(`${API}/golive/tasks/${t.task_id}/notes/${noteId}`, { withCredentials: true });
    reload();
  };
  const setPriority = async (noteId, priority) => {
    try {
      await axios.patch(`${API}/golive/tasks/${t.task_id}/notes/${noteId}`,
        { priority }, { withCredentials: true });
      reload();
    } catch (e) { alert('Could not update priority'); }
  };

  return (
    <div className="mt-2 rounded-lg border bg-muted/20 p-3 space-y-2" data-testid={`golive-notes-${t.task_id}`}>
      {(t.notes || []).map(n => {
        const p = PRIORITY_STYLES[n.priority] || PRIORITY_STYLES.none;
        return (
          <div key={n.id} className="text-xs border rounded p-2 flex items-start gap-2"
               style={{ background: p.bg, borderColor: p.border, borderLeftWidth: '4px', borderLeftColor: p.dot }}
               data-testid={`golive-note-${n.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-foreground whitespace-pre-wrap">{n.text}</div>
              <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                <span>{n.created_by_name} · {new Date(n.created_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                {/* Priority swatch picker on the existing note */}
                <span className="inline-flex items-center gap-1 ml-auto">
                  {PRIORITY_KEYS.filter(k => k !== 'none').map(k => {
                    const s = PRIORITY_STYLES[k];
                    const active = n.priority === k;
                    return (
                      <button key={k} onClick={() => setPriority(n.id, active ? 'none' : k)}
                              title={s.label}
                              data-testid={`golive-note-priority-${n.id}-${k}`}
                              className={`w-3.5 h-3.5 rounded-full border transition-all ${active ? 'ring-2 ring-offset-1' : 'opacity-60 hover:opacity-100'}`}
                              style={{ background: s.dot, borderColor: s.dot, '--tw-ring-color': s.dot }} />
                    );
                  })}
                </span>
              </div>
            </div>
            <button onClick={() => del(n.id)} className="text-muted-foreground hover:text-red-600 shrink-0" title="Delete note">
              <X className="w-3 h-3" />
            </button>
          </div>
        );
      })}
      <div className="flex gap-2">
        <div className="flex-1">
          <Textarea value={newNote} onChange={e => setNewNote(e.target.value)}
            placeholder='e.g. "Waiting on reply from Josh re after-hours config — chased 19/6"'
            rows={2} className="text-xs"
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) add(); }}
            data-testid={`golive-new-note-${t.task_id}`} />
          <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
            <span>Priority:</span>
            {PRIORITY_KEYS.map(k => {
              const s = PRIORITY_STYLES[k];
              const active = newPriority === k;
              return (
                <button key={k} onClick={() => setNewPriority(k)}
                        title={s.label}
                        data-testid={`golive-new-priority-${t.task_id}-${k}`}
                        className={`w-4 h-4 rounded-full border transition-all ${active ? 'ring-2 ring-offset-1' : 'opacity-60 hover:opacity-100'}`}
                        style={{ background: k === 'none' ? 'transparent' : s.dot, borderColor: s.dot, '--tw-ring-color': s.dot }} />
              );
            })}
            <kbd className="ml-auto font-mono">⌘+↵ to send</kbd>
          </div>
        </div>
        <button onClick={add} disabled={!newNote.trim() || saving}
          className="text-xs font-semibold px-3 py-2 rounded bg-[#0d2644] text-white disabled:opacity-50 self-start"
          data-testid={`golive-add-note-${t.task_id}`}>
          {saving ? '…' : 'Add note'}
        </button>
      </div>
    </div>
  );
}


/* ──────────────────────── Add task modal ───────────────────────── */
function AddTaskModal({ category, onClose, onAdded }) {
  const [title, setTitle] = useState('');
  const [owner, setOwner] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!title.trim() || busy) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/golive/tasks`, { category, title: title.trim(), owner: owner.trim() || null, due: due.trim() || null },
        { withCredentials: true });
      onAdded();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not add task');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-1">Add task</h3>
        <p className="text-xs text-muted-foreground mb-4">Category: <strong>{category}</strong></p>
        <div className="space-y-3">
          <Input placeholder="Task title (required)" value={title} onChange={e => setTitle(e.target.value)} autoFocus
                 data-testid="golive-new-task-title" />
          <Input placeholder="Owner (optional)" value={owner} onChange={e => setOwner(e.target.value)} />
          <Input placeholder='Due (optional) — e.g. "13 July"' value={due} onChange={e => setDue(e.target.value)} />
          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg border">Cancel</button>
          <button onClick={submit} disabled={!title.trim() || busy}
            className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#0d2644] text-white disabled:opacity-50"
            data-testid="golive-new-task-save">
            {busy ? 'Adding…' : 'Add task'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ──────────────────────── Import modal ─────────────────────────── */
function ImportModal({ onClose, onCommitted }) {
  const [stage, setStage] = useState('paste');  // paste | preview | committing | done
  const [text, setText] = useState('');
  const [preview, setPreview] = useState({ new: [], duplicates: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const doPreview = async () => {
    if (!text.trim()) return;
    setBusy(true); setErr('');
    try {
      const parsed = await axios.post(`${API}/golive/import/parse-text`, { text }, { withCredentials: true });
      const prev = await axios.post(`${API}/golive/import/preview`, { items: parsed.data.items }, { withCredentials: true });
      setPreview(prev.data);
      setStage('preview');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };
  const doCommit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/golive/import/commit`, { items: preview.new }, { withCredentials: true });
      setStage('done');
      setTimeout(() => onCommitted(), 800);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-1">Import tasks</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Paste a list of new tasks. Use headings (<code>#</code>, <code>##</code> or ALL-CAPS lines) to group by category. The system flags duplicates so you only add what's new.
        </p>

        {stage === 'paste' && (
          <>
            <Textarea value={text} onChange={e => setText(e.target.value)} rows={14}
              placeholder={`# STAFFING\n- Confirm Mark start date\n- Background check for new sonographer\n\n# IT\n- Install second printer in CT\n- Configure dual-screen on report station`}
              className="text-sm font-mono" data-testid="golive-import-text" />
            {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg border">Cancel</button>
              <button onClick={doPreview} disabled={!text.trim() || busy}
                className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#0d2644] text-white disabled:opacity-50"
                data-testid="golive-import-preview-btn">
                {busy ? 'Checking…' : 'Check for duplicates'}
              </button>
            </div>
          </>
        )}

        {stage === 'preview' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border p-3">
                <div className="text-sm font-bold mb-2 inline-flex items-center gap-1.5" style={{ color: '#15803d' }}>
                  <CheckCircle2 className="w-4 h-4" /> {preview.new.length} new task{preview.new.length===1?'':'s'} — will be added
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto text-xs">
                  {preview.new.length === 0 && <div className="text-muted-foreground">Nothing new to add.</div>}
                  {preview.new.map((it, i) => (
                    <div key={i} className="bg-green-50 border border-green-200 rounded px-2 py-1">
                      <div className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">{it.category}</div>
                      <div>{it.title}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-sm font-bold mb-2 inline-flex items-center gap-1.5" style={{ color: '#d97706' }}>
                  <AlertCircle className="w-4 h-4" /> {preview.duplicates.length} duplicate{preview.duplicates.length===1?'':'s'} — will be skipped
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto text-xs">
                  {preview.duplicates.length === 0 && <div className="text-muted-foreground">No duplicates.</div>}
                  {preview.duplicates.map((it, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">{it.category}</div>
                      <div className="line-through opacity-70">{it.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {err && <div className="text-xs text-red-600 mt-3">{err}</div>}
            <div className="flex justify-between items-center mt-4">
              <button onClick={() => setStage('paste')} className="text-sm font-semibold underline text-muted-foreground">← Back to edit</button>
              <div className="flex gap-2">
                <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg border">Cancel</button>
                <button onClick={doCommit} disabled={busy || !preview.new.length}
                  className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#15803d] text-white disabled:opacity-50"
                  data-testid="golive-import-commit-btn">
                  {busy ? 'Adding…' : `Add ${preview.new.length} new task${preview.new.length===1?'':'s'}`}
                </button>
              </div>
            </div>
          </>
        )}

        {stage === 'done' && (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: '#15803d' }} />
            <div className="text-base font-semibold">Tasks added</div>
            <div className="text-sm text-muted-foreground mt-1">Refreshing your checklist…</div>
          </div>
        )}
      </div>
    </div>
  );
}
